import { Dispatch } from 'redux';

import { Listing, fetchListing as apiFetchListing } from 'api/orgs';
import { ApiImportedListing } from 'api/volops';
import { getObject as algoliaGetObject } from 'utils/algoliaSearch';
import getSearchConfig from 'utils/search/getSearchConfig';
import { getVolopsSearchIndex } from 'utils/search/getVolopsSearchIndex';

import { MainStoreErrorAction, setError } from './error';

// Types

export type MainStoreListingsState = {
  byId: Record<
    string,
    Listing & {
      isFetching: boolean;
      isUnpublished: boolean;
    }
  >;
};

type FetchListingRequestAction = {
  type: 'FETCH_LISTING_REQUEST';
  listingId: string;
};

type FetchListingSuccessAction = {
  type: 'FETCH_LISTING_SUCCESS';
  listing: Listing;
};

type FetchImportedListingSuccessAction = {
  type: 'FETCH_IMPORTED_LISTING_SUCCESS';
  listing: ApiImportedListing;
};

type SetListingUnpublishedAction = {
  type: 'SET_LISTING_UNPUBLISHED';
  listingId: string;
  isUnpublished: boolean;
};

export type MainStoreListingsAction =
  | FetchListingRequestAction
  | FetchListingSuccessAction
  | FetchImportedListingSuccessAction
  | SetListingUnpublishedAction;

// Reducer

export function listingsReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreListingsState = {
    byId: {},
  },
  action: MainStoreListingsAction
) {
  switch (action.type) {
    case 'FETCH_LISTING_REQUEST':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listingId]: {
            ...state.byId[action.listingId],
            id: action.listingId,
            isFetching: true,
          },
        },
      };

    case 'FETCH_LISTING_SUCCESS':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listing.id]: {
            ...action.listing,
            isFetching: false,
            isUnpublished: false,
          },
        },
      };

    case 'SET_LISTING_UNPUBLISHED':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listingId]: {
            isFetching: false,
            isUnpublished: true,
          },
        },
      };

    case 'FETCH_IMPORTED_LISTING_SUCCESS':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listing.objectID]: {
            ...action.listing,
            id: action.listing.objectID,
            isFetching: false,
          },
        },
      };

    default:
      return state;
  }
}

// Actions

const fetchListingRequest = (listingId: string): FetchListingRequestAction => ({
  type: 'FETCH_LISTING_REQUEST',
  listingId,
});

export const setListingUnpublished = (
  listingId: string,
  isUnpublished: boolean
): SetListingUnpublishedAction => ({
  type: 'SET_LISTING_UNPUBLISHED',
  listingId,
  isUnpublished,
});

const fetchListingSuccess = (listing: Listing): FetchListingSuccessAction => ({
  type: 'FETCH_LISTING_SUCCESS',
  listing,
});

const fetchImportedListingSuccess = (
  listing: ApiImportedListing
): FetchImportedListingSuccessAction => ({
  type: 'FETCH_IMPORTED_LISTING_SUCCESS',
  listing,
});

export const fetchListing =
  (listingType: string, listingId: string, force = false) =>
  (
    dispatch: Dispatch,
    getState: () => { listings: MainStoreListingsState }
  ) => {
    const state = getState();
    const listing = state.listings.byId[listingId];

    if (!listing || (force && !listing.isFetching)) {
      dispatch(fetchListingRequest(listingId));
      return apiFetchListing(listingType, listingId).then((fetchedListing) => {
        dispatch(fetchListingSuccess(fetchedListing));
        return fetchedListing;
      });
    }

    return Promise.resolve(listing);
  };
export const fetchImportedListing =
  (listingId: string, force = false) =>
  (
    dispatch: Dispatch<MainStoreListingsAction | MainStoreErrorAction>,
    getState: () => { listings: MainStoreListingsState }
  ) => {
    const state = getState();
    const listing = state.listings.byId[listingId];

    if (!listing || (force && !listing.isFetching)) {
      dispatch(fetchListingRequest(listingId));
      const searchConfig = getSearchConfig();
      const index = getVolopsSearchIndex(null, 'relevance');
      algoliaGetObject(searchConfig, index, listingId, ['*'])
        .then((fetchedListing) => {
          dispatch(
            fetchImportedListingSuccess(
              // @ts-expect-error TS(2345): Argument of type 'ImportedListing | null | undefin... Remove this comment to see the full error message
              fetchedListing as ApiImportedListing | null | undefined
            )
          );
        })
        .catch((e) => {
          dispatch(setError(e));
        });
    }
  };
export const updateListing = fetchListingSuccess;
